import React, { useState, useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';
import {
  Box, Collapse, IconButton, Table, TableBody, TableCell, TableHead, TableRow,
  Typography, Tooltip, Menu, MenuItem as MUIMenuItem, ListItemIcon, ListItemText, Stack, Button, Paper
} from '@mui/material';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';

import {
  getColumnLabel, formatCellValue, renderAuthStatus, renderCanceladoERP,
  ActiveStatusChip, normalizeValue, CANCELADO_ERP_BG, renderStatusColor
} from './TableFormatters';

const focusRing = { outline: '2px solid #3b82f6', outlineOffset: '2px', borderRadius: '6px' };

export const StyledTableRow = styled(TableRow)(({ theme }) => ({
  transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
  borderBottom: `1px solid #f3f4f6`,
  '&:hover': { backgroundColor: '#f9fafb' },
}));

export const CircleButton = styled(Button)(({ theme, isdisabled, buttoncolor, buttonfontcolor }) => ({
  backgroundColor: isdisabled === 'true' ? 'transparent' : (buttoncolor ? `${buttoncolor}15` : 'rgba(0, 0, 0, 0.04)'),
  color: isdisabled === 'true' ? '#cbd5e1' : (buttoncolor || '#475569'),
  borderRadius: '6px',
  width: 24,
  height: 24,
  minWidth: 24,
  padding: 0,
  marginRight: 0,
  pointerEvents: isdisabled === 'true' ? 'none' : 'auto',
  opacity: isdisabled === 'true' ? 0.35 : 0.9,
  boxShadow: 'none',
  border: 'none',
  transition: 'all 0.15s ease',
  '& svg': { fontSize: '0.92rem' },
  '&:hover': {
    backgroundColor: isdisabled === 'true' ? 'transparent' : (buttoncolor || '#0f172a'),
    color: isdisabled === 'true' ? '#cbd5e1' : '#ffffff',
    opacity: 1,
    transform: 'scale(1.08)',
  },
}));

export const ActionsCell = styled(TableCell, { shouldForwardProp: (p) => p !== 'sticky' && p !== 'edgeShadow' })(({ sticky, edgeShadow }) => ({
  minWidth: 0, width: '1%', whiteSpace: 'nowrap', padding: '6px',
  position: sticky ? 'sticky' : 'static', right: sticky ? 0 : 'auto', zIndex: 3,
  backgroundColor: 'inherit', backgroundImage: 'inherit',
  boxShadow: sticky ? '-2px 0 8px rgba(0,0,0,0.03)' : 'none',
}));

export const styledPill = {
  display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 10px',
  borderRadius: '999px', fontSize: '12px', fontWeight: 600,
  backgroundColor: '#f3f4f6', color: '#4b5563', border: '1px solid #e5e7eb'
};

export const CellText = ({ children, lines = 1 }) => {
  const text = children ?? '';
  const ref = useRef(null);
  const [overflow, setOverflow] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => {
      if (expanded) { setOverflow(false); return; }
      if (lines > 1) setOverflow(el.scrollHeight > el.clientHeight + 1);
      else setOverflow(el.scrollWidth > el.clientWidth + 1);
    };
    check();
    let ro;
    if ('ResizeObserver' in window) { ro = new ResizeObserver(check); ro.observe(el); }
    window.addEventListener('resize', check);
    return () => { window.removeEventListener('resize', check); if (ro) ro.disconnect(); };
  }, [text, expanded, lines]);

  const toggle = () => { if (overflow || expanded) setExpanded(v => !v); };
  const collapsedStyles = lines > 1
    ? { display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: lines }
    : { whiteSpace: 'nowrap', textOverflow: 'ellipsis' };

  return (
    <Box sx={{ position: 'relative', maxWidth: 260, mx: 'auto', minWidth: 0 }}>
      <Box
        ref={ref} onClick={toggle}
        sx={{
          cursor: overflow ? 'pointer' : 'default', lineHeight: 1.4, wordBreak: 'break-word',
          overflow: 'hidden', color: '#374151',
          ...(expanded ? { whiteSpace: 'normal' } : collapsedStyles),
        }}
      >
        {String(text)}
      </Box>
      {(overflow || expanded) && (
        <Box onClick={(e) => { e.stopPropagation(); toggle(); }} sx={{ cursor: 'pointer', display: 'inline-flex', pt: 0.5 }}>
          {expanded ? <ExpandLess sx={{ fontSize: 16, color: '#3b82f6' }} /> : <ExpandMore sx={{ fontSize: 16, color: '#6b7280' }} />}
        </Box>
      )}
    </Box>
  );
};

export const stickyCellSx = (meta, isHeader = false) => {
  if (!meta) return {};
  return {
    position: 'sticky', left: meta.left, zIndex: isHeader ? meta.zIndex + 2 : meta.zIndex,
    minWidth: meta.width, width: meta.width, maxWidth: meta.width, background: 'inherit',
    boxShadow: meta.isLastSticky ? '2px 0 8px rgba(0,0,0,0.04)' : 'none',
  };
};

export const RowActions = React.memo(({ buttons = [], row, compact = false, maxInline }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const items = buttons.map((b) => {
    const isVisible = typeof b.isVisible === 'function' ? b.isVisible(row) : b.isVisible;
    if (isVisible === false) return null;
    const disabled = typeof b.isDisabled === 'function' ? b.isDisabled(row) : b.isDisabled;
    return {
      id: b.id,
      label: typeof b.label === 'function' ? b.label(row) : b.label,
      icon: typeof b.icon === 'function' ? b.icon(row) : b.icon,
      color: typeof b.buttonColor === 'function' ? b.buttonColor(row) : b.buttonColor,
      fontColor: typeof b.buttonFontColor === 'function' ? b.buttonFontColor(row) : b.buttonFontColor,
      disabled,
      onClick: () => b.onClick(row),
    };
  }).filter(Boolean);

  // Detectar si es pantalla grande (xl+) con JS puro
  const [isXLarge, setIsXLarge] = useState(() => window.innerWidth >= 1280);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1280px)');
    const handler = (e) => setIsXLarge(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const renderBtn = (it, sz = {}) => (
    <Tooltip key={it.id} title={it.label} arrow disableInteractive>
      <span style={{ display: 'inline-flex' }}>
        <CircleButton
          isdisabled={it.disabled ? 'true' : 'false'}
          buttoncolor={it.color}
          buttonfontcolor={it.fontColor}
          variant="contained"
          onClick={(e) => { e.stopPropagation(); it.onClick(); }}
          disabled={it.disabled}
          sx={{
            transition: 'all 0.15s cubic-bezier(0.34, 1.56, 0.64, 1)',
            '&:hover': {
              transform: 'scale(1.15)',
              zIndex: 5,
            },
            ...sz
          }}
        >
          {it.icon}
        </CircleButton>
      </span>
    </Tooltip>
  );

  const overflowMenu = (overflowItems) => (
    <>
      <Tooltip title="Más acciones" arrow disableInteractive>
        <span>
          <CircleButton
            isdisabled="false"
            buttoncolor="#64748b"
            buttonfontcolor="#ffffff"
            variant="contained"
            onClick={(e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); }}
            sx={isXLarge
              ? {
                  width: 22, height: 22, minWidth: 22,
                  bgcolor: 'rgba(100,116,139,0.1)', color: '#64748b',
                  transition: 'all 0.15s ease',
                  '&:hover': { transform: 'scale(1.15)', bgcolor: '#64748b', color: '#fff' }
                }
              : {
                  width: 24, height: 24, minWidth: 24,
                  bgcolor: 'rgba(100,116,139,0.1)', color: '#64748b',
                  transition: 'all 0.15s ease',
                  '&:hover': { transform: 'scale(1.15)', bgcolor: '#64748b', color: '#fff' }
                }
            }
          >
            <MoreHoriz sx={{ fontSize: isXLarge ? '0.85rem' : '0.95rem' }} />
          </CircleButton>
        </span>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{ sx: { borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', border: '1px solid #e2e8f0', minWidth: 190 } }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {overflowItems.map(it => (
          <MUIMenuItem
            key={it.id}
            onClick={(e) => { e.stopPropagation(); it.onClick(); setAnchorEl(null); }}
            disabled={it.disabled}
            sx={{ gap: 1, fontSize: '0.78rem', py: 0.8, opacity: it.disabled ? 0.4 : 1 }}
          >
            <ListItemIcon sx={{ minWidth: 28, color: it.color, '& svg': { fontSize: '1rem' } }}>{it.icon}</ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: '0.78rem', fontWeight: 600 }}>{it.label}</ListItemText>
          </MUIMenuItem>
        ))}
      </Menu>
    </>
  );

  // Modo compacto o cuando maxInline es limitado (ej. 1, 2 o 3 botones en una sola fila + '...')
  if (compact || (maxInline && maxInline <= 3)) {
    const limit = Math.max(1, maxInline || 3);
    const inline = items.slice(0, limit);
    const overflow = items.slice(limit);
    const btnSzCompact = { width: 22, height: 22, minWidth: 22, '& svg': { fontSize: '0.85rem' } };

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, justifyContent: 'center', py: 0.2 }}>
        {inline.map(it => renderBtn(it, btnSzCompact))}
        {overflow.length > 0 && overflowMenu(overflow)}
      </Box>
    );
  }

  if (isXLarge) {
    // Pantalla GRANDE (1280px+): 4 elementos por fila (Fila 1: 4 botones, Fila 2: 3 botones + desplegable)
    const btnSzLarge = { width: 24, height: 24, minWidth: 24, '& svg': { fontSize: '0.9rem' } };
    const inline = items.slice(0, 7);
    const overflow = items.slice(7);
    const row1 = inline.slice(0, 4);
    const row2 = inline.slice(4, 7);
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, justifyContent: 'center', py: 0.2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
          <Box sx={{ display: 'flex', gap: 0.4 }}>{row1.map(it => renderBtn(it, btnSzLarge))}</Box>
          <Box sx={{ display: 'flex', gap: 0.4, alignItems: 'center' }}>
            {row2.map(it => renderBtn(it, btnSzLarge))}
            {overflow.length > 0 ? (
              <>
                <Tooltip title="Más acciones" arrow disableInteractive>
                  <span>
                    <CircleButton
                      isdisabled="false"
                      buttoncolor="#64748b"
                      buttonfontcolor="#ffffff"
                      variant="contained"
                      onClick={(e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); }}
                      sx={{
                        width: 24, height: 24, minWidth: 24,
                        bgcolor: 'rgba(100,116,139,0.1)', color: '#64748b',
                        transition: 'all 0.15s ease',
                        '&:hover': { transform: 'scale(1.15)', bgcolor: '#64748b', color: '#fff' }
                      }}
                    >
                      <MoreHoriz sx={{ fontSize: '0.9rem' }} />
                    </CircleButton>
                  </span>
                </Tooltip>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={() => setAnchorEl(null)}
                  onClick={(e) => e.stopPropagation()}
                  PaperProps={{ sx: { borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', border: '1px solid #e2e8f0', minWidth: 190 } }}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                  {overflow.map(it => (
                    <MUIMenuItem
                      key={it.id}
                      onClick={(e) => { e.stopPropagation(); it.onClick(); setAnchorEl(null); }}
                      disabled={it.disabled}
                      sx={{ gap: 1, fontSize: '0.78rem', py: 0.8, opacity: it.disabled ? 0.4 : 1 }}
                    >
                      <ListItemIcon sx={{ minWidth: 28, color: it.color, '& svg': { fontSize: '1rem' } }}>{it.icon}</ListItemIcon>
                      <ListItemText primaryTypographyProps={{ fontSize: '0.78rem', fontWeight: 600 }}>{it.label}</ListItemText>
                    </MUIMenuItem>
                  ))}
                </Menu>
              </>
            ) : (
              // Si no hay más de 7, colocar el botón de tres puntos en la posición #4 de la fila 2 para completar 4
              items.length > 4 && items.length <= 7 && (
                <Tooltip title="Más acciones" arrow disableInteractive>
                  <span>
                    <CircleButton
                      isdisabled="false"
                      buttoncolor="#64748b"
                      buttonfontcolor="#ffffff"
                      variant="contained"
                      onClick={(e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); }}
                      sx={{
                        width: 24, height: 24, minWidth: 24,
                        bgcolor: 'rgba(100,116,139,0.1)', color: '#64748b',
                        transition: 'all 0.15s ease',
                        '&:hover': { transform: 'scale(1.15)', bgcolor: '#64748b', color: '#fff' }
                      }}
                    >
                      <MoreHoriz sx={{ fontSize: '0.9rem' }} />
                    </CircleButton>
                  </span>
                </Tooltip>
              )
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  // Pantalla CHICA / Laptop (< 1280px): 3 botones normales chicos (20px) + el 4to desplegable '...'
  const btnSzSmall = { width: 20, height: 20, minWidth: 20, '& svg': { fontSize: '0.78rem' } };
  const inline = items.slice(0, 3);
  const overflow = items.slice(3);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, justifyContent: 'center', py: 0.2 }}>
      {inline.map(it => renderBtn(it, btnSzSmall))}
      {overflow.length > 0 && (
        <>
          <Tooltip title="Más acciones" arrow disableInteractive>
            <span>
              <CircleButton
                isdisabled="false"
                buttoncolor="#64748b"
                buttonfontcolor="#ffffff"
                variant="contained"
                onClick={(e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); }}
                sx={{
                  width: 20, height: 20, minWidth: 20,
                  bgcolor: 'rgba(100,116,139,0.1)', color: '#64748b',
                  transition: 'all 0.15s ease',
                  '&:hover': { transform: 'scale(1.15)', bgcolor: '#64748b', color: '#fff' }
                }}
              >
                <MoreHoriz sx={{ fontSize: '0.8rem' }} />
              </CircleButton>
            </span>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            onClick={(e) => e.stopPropagation()}
            PaperProps={{ sx: { borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', border: '1px solid #e2e8f0', minWidth: 190 } }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            {overflow.map(it => (
              <MUIMenuItem
                key={it.id}
                onClick={(e) => { e.stopPropagation(); it.onClick(); setAnchorEl(null); }}
                disabled={it.disabled}
                sx={{ gap: 1, fontSize: '0.78rem', py: 0.8, opacity: it.disabled ? 0.4 : 1 }}
              >
                <ListItemIcon sx={{ minWidth: 28, color: it.color, '& svg': { fontSize: '1rem' } }}>{it.icon}</ListItemIcon>
                <ListItemText primaryTypographyProps={{ fontSize: '0.78rem', fontWeight: 600 }}>{it.label}</ListItemText>
              </MUIMenuItem>
            ))}
          </Menu>
        </>
      )}
    </Box>
  );
});



export const Row = React.memo(({
  row, buttons, isExpanded, onToggleExpand, isPaginationChange,
  orderedKeys, density, isRecent, stickyActions, maxInlineActions,
  columnLabels, stickyLeftMeta, rowKey,
}) => {
  if (!row || Object.keys(row).length === 0) return null;
  const canceladoERP = Number(row.CANCELADO_ERP) === 1;
  const statusColor = renderStatusColor(row.ESTATUS, row.CONFIRMARPRECIO, row.CANCELADO_ERP);
  const rowBg = canceladoERP ? CANCELADO_ERP_BG : (isRecent(row) ? 'linear-gradient(90deg,#eff6ff 0,#ffffff 20%)' : (statusColor || '#ffffff'));
  const isGrad = typeof rowBg === 'string' && rowBg.startsWith('linear-gradient');
  const baseBg = isGrad ? '#ffffff' : rowBg;
  const isInactivo = normalizeValue(row.ACTIVO, 'boolean') === 0;
  const finalBg = isInactivo ? '#f1f5f9' : baseBg;
  const finalBgImage = isInactivo ? 'none' : (isGrad ? rowBg : 'none');
  const hasExpand = row.partidas?.length > 0;
  const densityPadding = density === 'compact' ? '5px 10px' : '7px 12px';

  return (
    <>
      <StyledTableRow
        data-rowkey={rowKey}
        sx={{
          backgroundColor: finalBg, backgroundImage: finalBgImage,
          '& > td, & > th': { backgroundColor: finalBg, backgroundImage: finalBgImage },
          opacity: isInactivo ? 0.72 : 1,
        }}
      >
        {hasExpand && (
          <TableCell sx={{ padding: 0, width: 44, ...stickyCellSx(stickyLeftMeta?.__expand__) }}>
            <IconButton size="small" onClick={onToggleExpand} sx={{ '&:focus-visible': focusRing }}>
              {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          </TableCell>
        )}

        {orderedKeys.map((key) => (
          <TableCell align="center" key={`${rowKey}-${key}`} sx={{ fontWeight: 500, color: '#1f2937', padding: densityPadding, ...(stickyLeftMeta?.[key] ? stickyCellSx(stickyLeftMeta[key]) : {}), }}>
            {key === 'ACTIVO' ? <ActiveStatusChip value={row[key]} />
              : key === 'CANCELADO_ERP' ? <Box sx={{ display: 'inline-flex' }}>{renderCanceladoERP(row[key])}</Box>
                : (key.startsWith('AUTNIV') || key.startsWith('AUTNIV') || key === 'CONFIRMARPRECIO') ? renderAuthStatus(row[key])
                  : <CellText>{formatCellValue(key, row[key])}</CellText>}
          </TableCell>
        ))}

        {buttons?.length > 0 && (
          <ActionsCell sticky={stickyActions} edgeShadow align="center">
            <RowActions buttons={buttons} row={row} maxInline={maxInlineActions} />
          </ActionsCell>
        )}
      </StyledTableRow>

      <StyledTableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0, border: 'none' }} colSpan={orderedKeys.length + (buttons?.length ? 1 : 0) + (hasExpand ? 1 : 0)}>
          <Collapse in={isExpanded} timeout={isPaginationChange ? 0 : 'auto'} unmountOnExit>
            <Box sx={{ m: 1.5, p: 1.5, bgcolor: '#f9fafb', borderRadius: 2, border: '1px solid #e5e7eb' }}>
              {hasExpand ? (
                <Table size="small" sx={{ bgcolor: 'white', borderRadius: 1, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f3f4f6' }}>
                      {Object.keys(row.partidas[0] || {}).map((col, index) => (
                        <TableCell align="center" key={index} sx={{ fontWeight: 600, color: '#4b5563', fontSize: '0.75rem' }}>
                          {getColumnLabel(col, columnLabels)}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {row.partidas.map((item, idx) => (
                      <TableRow key={idx}>
                        {Object.keys(row.partidas[0] || {}).map((k, i) => (
                          <TableCell align="center" key={i} sx={{ color: '#374151', padding: '8px' }}>
                            {k === 'ESTADO' ? renderAuthStatus(item[k], item.MOTIVO_CANCELACION) : <CellText>{formatCellValue(k, item[k])}</CellText>}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : <Box sx={{ textAlign: 'center' }}><span style={styledPill}>Sin Registros</span></Box>}
            </Box>
          </Collapse>
        </TableCell>
      </StyledTableRow>
    </>
  );
});

export const MobileCardRow = React.memo(({
  row, orderedKeys, buttons, isRecent, maxInlineActions = 3, openDetails, mobileCard = {}, columnLabels = {},
}) => {
  const [open, setOpen] = useState(false);
  const [showMore, setShowMore] = useState(false);

  // Simplified for speed, mimics original logic
  const canceladoERP = Number(row.CANCELADO_ERP) === 1;
  const isInactive = normalizeValue(row.ACTIVO, 'boolean') === 0;

  return (
    <Paper elevation={0} sx={{ p: 2, mb: 1.5, borderRadius: 3, opacity: isInactive ? 0.7 : 1, border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', transition: 'transform 0.2s', '&:active': { transform: 'scale(0.98)' } }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} onClick={() => openDetails?.(row)} sx={{ cursor: 'pointer', color: '#111827' }}>
            {getColumnLabel(orderedKeys[0], columnLabels)}: {formatCellValue(orderedKeys[0], row[orderedKeys[0]])}
          </Typography>
        </Box>
        {buttons?.length > 0 && <RowActions buttons={buttons} row={row} maxInline={maxInlineActions} />}
      </Stack>

      <Stack spacing={1}>
        {orderedKeys.slice(1, showMore ? undefined : 4).map(k => (
          <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <Typography variant="body2" color="#6b7280">{getColumnLabel(k, columnLabels)}</Typography>
            <Typography variant="body2" fontWeight={600} color="#1f2937" textAlign="right">
              {k === 'ACTIVO' ? <ActiveStatusChip value={row[k]} /> : (k === 'CANCELADO_ERP' ? renderCanceladoERP(row[k]) : formatCellValue(k, row[k]))}
            </Typography>
          </Box>
        ))}
      </Stack>

      {orderedKeys.length > 4 && (
        <Button size="small" onClick={() => setShowMore(v => !v)} sx={{ mt: 1, width: '100%', color: '#3b82f6', bgcolor: '#eff6ff', borderRadius: 2 }}>
          {showMore ? 'Ver menos' : 'Ver más'}
        </Button>
      )}
    </Paper>
  );
});
